import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

// Populates and shows the page-level #settingInfoModal when an admin clicks a
// setting Label on /admin/system/app-settings. There is ONE modal element on the
// page; this controller swaps its body text per click rather than rendering
// one hidden modal per setting up front.
//
// Data values on the Label button:
//   data-setting-info-modal-var-value         — the AppSetting var (e.g. "fraud_alert_emails")
//   data-setting-info-modal-label-value       — the pretty label
//   data-setting-info-modal-description-value — the per-setting "what this controls" prose
//   data-setting-info-modal-edit-url-value    — where the modal's "Edit Setting" button points;
//                                               blank for read-only groups (modal hides the button)
//   data-setting-info-modal-edit-method-value — "get" (edit) or "post" (materialize). When "post"
//                                               we attach data-turbo-method=post to the anchor so
//                                               Turbo issues a POST with the CSRF token (the
//                                               materialize route is POST-only — see routes.rb).
export default class extends Controller {
  static values = {
    var: String,
    label: String,
    description: String,
    editUrl: String,
    editMethod: String,
  }

  open() {
    // Label is rendered as <button type="button"> (no browser default action to
    // cancel), so we don't call preventDefault. The modal element lives at the
    // page level, OUTSIDE this controller's root (the <button>), so a Stimulus
    // target wouldn't resolve — getElementById is the correct singleton lookup.
    const modal = document.getElementById("settingInfoModal")
    if (!modal) return

    // The title is icon + a text-only span (shared shell `title_text_attrs`):
    // writing the span keeps the filled header icon beside it (MODAL-1).
    const title = modal.querySelector("#settingInfoModalTitle") || modal.querySelector("#settingInfoModalLabel")
    title.textContent = this.labelValue
    modal.querySelector("#settingInfoModalKey").textContent = this.varValue
    modal.querySelector("#settingInfoModalDescription").textContent =
      this.descriptionValue || "No description authored yet."

    const editLink = modal.querySelector("#settingInfoModalEditLink")
    if (editLink) {
      if (this.editUrlValue) {
        editLink.setAttribute("href", this.editUrlValue)
        if (this.editMethodValue === "post") {
          editLink.setAttribute("data-turbo-method", "post")
        } else {
          editLink.removeAttribute("data-turbo-method")
        }
        editLink.hidden = false
      } else {
        editLink.hidden = true
        editLink.removeAttribute("data-turbo-method")
      }
    }

    Modal.getOrCreateInstance(modal).show()
  }

  // Actions-cell pencil entry point: open the modal AND go straight into edit
  // mode, instead of the layout-less full-page /edit screen. We reuse the
  // modal's own "Edit Setting" link rather than re-implementing its Turbo-Frame
  // + GET/POST (materialize) wiring, so both entry points share one edit path.
  openEdit() {
    this.open()

    const editLink = document.getElementById("settingInfoModalEditLink")
    if (editLink && !editLink.hidden) editLink.click()
  }
}
